class StickyHeader {
    private header: HTMLElement;
    private lastScrollY: number = 0;
    private scrollThreshold: number = 100; // Start showing/hiding after 100px scroll
    private isHeaderVisible: boolean = true;

    constructor(element: HTMLElement) {
        this.header = element;
        this.init();
    }

    private init() {
        // Initial state - header is visible at top
        this.lastScrollY = window.scrollY;
        this.updateHeaderTheme();

        // Listen to scroll events
        window.addEventListener('scroll', () => this.handleScroll(), { passive: true });
    }

    private handleScroll() {
        const currentScrollY = window.scrollY;
        this.updateHeaderTheme();

        // Don't do anything if we're at the very top
        if (currentScrollY < this.scrollThreshold) {
            this.showHeader();
            this.lastScrollY = currentScrollY;
            return;
        }

        // Determine scroll direction
        const scrollingDown = currentScrollY > this.lastScrollY;
        const scrollingUp = currentScrollY < this.lastScrollY;

        if (scrollingDown && this.isHeaderVisible) {
            // Hide header when scrolling down
            this.hideHeader();
        } else if (scrollingUp && !this.isHeaderVisible) {
            // Show header when scrolling up
            this.showHeader();
        }

        this.lastScrollY = currentScrollY;
    }

    private showHeader() {
        this.isHeaderVisible = true;
        this.header.style.transition = 'transform 0.3s ease-in-out';
        this.header.style.transform = 'translateY(0)';

        let hasUpdatedTheme = false;
        const onTransitionEnd = (event: TransitionEvent) => {
            if (event.target !== this.header || event.propertyName !== 'transform') return;
            if (hasUpdatedTheme) return;

            hasUpdatedTheme = true;
            this.header.removeEventListener('transitionend', onTransitionEnd);
            this.updateHeaderTheme();
        };

        this.header.addEventListener('transitionend', onTransitionEnd);

        // Fallback if transitionend does not fire (e.g. reduced motion or no actual transform change)
        window.setTimeout(() => {
            if (hasUpdatedTheme) return;
            hasUpdatedTheme = true;
            this.header.removeEventListener('transitionend', onTransitionEnd);
            this.updateHeaderTheme();
        }, 350);
    }

    private hideHeader() {
        const mobileMenuToggle = document.getElementById('mobile-menu-toggle');
        if (mobileMenuToggle?.getAttribute('aria-expanded') === 'true') {
            this.showHeader();
            return;
        }

        this.isHeaderVisible = false;
        this.header.style.transform = 'translateY(-100%)';
        this.header.style.transition = 'transform 0.3s ease-in-out';
    }

    private updateHeaderTheme() {
        this.header.classList.toggle('menu-light', this.isHeaderOverElementWithClass('dark'));
        this.header.classList.toggle('logo-light', this.isHeaderOverElementWithClass('blue'));
    }

    private isHeaderOverElementWithClass(className: string): boolean {
        const rect = this.header.getBoundingClientRect();
        const x = rect.left + rect.width / 2;
        const y = rect.top + rect.height / 2;

        if (rect.width <= 0 || rect.height <= 0 || x < 0 || y < 0 || x > window.innerWidth || y > window.innerHeight) {
            return false;
        }

        const previousPointerEvents = this.header.style.pointerEvents;
        this.header.style.pointerEvents = 'none';

        const elements = document.elementsFromPoint(x, y) as HTMLElement[];

        this.header.style.pointerEvents = previousPointerEvents;

        const underlyingElement = elements.find(
            (element) => element !== this.header && !this.header.contains(element)
        );

        return underlyingElement?.closest(`.${className}`) !== null;
    }

    public destroy() {
        window.removeEventListener('scroll', () => this.handleScroll());
    }
}

document.addEventListener("DOMContentLoaded", () => {
    const header = document.querySelector("header") as HTMLElement;
    if (header) {
        new StickyHeader(header);
    }
});

export default StickyHeader;
